import {useState} from 'react'
import {TextInput, Select, IconButton, BranchName} from '@primer/react'
import {PlusIcon, TrashIcon} from '@primer/octicons-react'
import type {RegisteredRuleSchemaComponent} from '../../types/rules-types'
import {Blankslate} from '../Blankslate'
import {BorderBox} from '../BorderBox'

import styles from './RestrictedCommitsList.module.css'
import {clsx} from 'clsx'

const DEFAULT_VALUE: RestrictedCommit[] = []
type RestrictedCommit = {oid: string; reason?: string}
const basicReasons = ['Contains secret(s)', 'Contains sensitive information', 'Company IP']
const reasons = [...basicReasons, 'Other']

export function RestrictedCommitsList({value, onValueChange, readOnly}: RegisteredRuleSchemaComponent) {
  const [oid, setOid] = useState('')
  const [reason, setReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const restrictedCommits = (value as RestrictedCommit[]) || DEFAULT_VALUE

  const addRestrictedCommitToList = (commit: RestrictedCommit) => {
    if (restrictedCommits.find(existing => existing.oid === commit.oid)) return restrictedCommits
    if (commit.oid === '') return restrictedCommits
    onValueChange?.([...restrictedCommits, commit])
  }

  const removeRestrictedCommitFromList = (restrictedCommitToRemove: RestrictedCommit) => {
    onValueChange?.(restrictedCommits.filter(existing => existing.oid !== restrictedCommitToRemove.oid))
  }

  return (
    <BorderBox>
      {readOnly ? null : (
        <div className={clsx('Box-header', styles.HeaderGrid)}>
          <div className={styles.Section}>
            <h5 className={styles.SectionHeading}>Add full or abbreviated commit hash</h5>
            <TextInput
              placeholder="Commit ID"
              block
              value={oid}
              onChange={e => setOid(e.target.value)}
              className={styles.CommitInput}
            />
          </div>
          <div className={styles.Section}>
            <h5 className={styles.SectionHeading}>Reason</h5>
            <div className={styles.ReasonControls}>
              <div>
                <Select placeholder="Select reason" value={reason} onChange={e => setReason(e.target.value)}>
                  {reasons.map(restrictReason => (
                    <Select.Option key={restrictReason} value={restrictReason}>
                      {restrictReason}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <IconButton
                  type="button"
                  aria-label="Add"
                  size="medium"
                  icon={PlusIcon}
                  onClick={() => {
                    setOid('')
                    setOtherReason('')
                    setReason('')
                    addRestrictedCommitToList({oid, reason: reason === 'Other' ? otherReason : reason})
                  }}
                />
              </div>
            </div>
          </div>
          {reason === 'Other' ? (
            <div className={styles.OtherReasonContainer}>
              <TextInput
                className="width-full"
                placeholder="Other reason"
                value={otherReason}
                onChange={e => setOtherReason(e.target.value)}
              />
            </div>
          ) : null}
        </div>
      )}
      {restrictedCommits.length === 0 ? (
        <Blankslate heading="No commit IDs have been added yet">
          {readOnly
            ? undefined
            : 'Add a commit ID restriction to prevent specific commits from ever being accepted into target branches'}
        </Blankslate>
      ) : (
        <ul>
          {restrictedCommits.map(restrictedCommit => (
            <li className="Box-row" key={restrictedCommit.oid}>
              <div className={clsx('flex-direction-row flex-justify-between', styles.CommitItem)}>
                <div>
                  <BranchName>{restrictedCommit.oid}</BranchName>
                </div>
                {!readOnly ? (
                  <IconButton
                    type="button"
                    aria-label="Remove"
                    variant="invisible"
                    size="small"
                    icon={TrashIcon}
                    onClick={() => {
                      removeRestrictedCommitFromList(restrictedCommit)
                    }}
                  />
                ) : null}
              </div>
              {restrictedCommit.reason && (
                <div>
                  <p>{restrictedCommit.reason}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </BorderBox>
  )
}

try{ RestrictedCommitsList.displayName ||= 'RestrictedCommitsList' } catch {}